import React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

const PAPER_SX = {
  m: { xs: 2, sm: 4 },
  width: { xs: "calc(100% - 2rem)", sm: "calc(100% - 4rem)" }
};

const TITLE_SX = { px: { xs: 2.5, sm: 3 }, pt: { xs: 2.5, sm: 2 }, pb: 1, fontWeight: 700 };
const CONTENT_SX = { px: { xs: 2.5, sm: 3 } };

const ACTIONS_SX = {
  px: { xs: 2.5, sm: 3 },
  pt: 1,
  pb: 2.5,
  gap: 1,
  flexDirection: { xs: "column-reverse", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" }
};

const BUTTON_SX = { textTransform: "none", minHeight: { xs: "2.75rem", sm: "auto" } };

const BLOCKED_REASON = {
  CLUB_ONLY: "The club handles cancellations for this",
  POLICY_WINDOW: "It's too close to the start time to cancel online, so the club has to cancel this"
};

function ClubBookingCancelDialog({ cancel }) {
  const { state, close, confirmCancel, leaveOut } = cancel;
  const { open, target, preview, loading, submitting } = state;
  const kind = target?.kind === "class" ? "class" : "lesson";
  const outsidePack = !!target?.outsidePack;

  return (
    <Dialog open={open} onClose={submitting ? undefined : close} maxWidth="xs" fullWidth PaperProps={{ sx: PAPER_SX }}>
      {loading || !preview ? (
        <DialogContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}>
          <CircularProgress size="1.5rem" />
          <DialogContentText>Checking the club&apos;s cancellation policy…</DialogContentText>
        </DialogContent>
      ) : preview.canCancel ? (
        <>
          <DialogTitle sx={TITLE_SX}>Cancel this {kind}?</DialogTitle>
          <DialogContent sx={CONTENT_SX}>
            <DialogContentText sx={{ mb: 1.5 }}>
              The club booked <strong>{target.label}</strong> for you and it isn&apos;t paid yet.{" "}
              {outsidePack ? "Cancel it if you can't make it." : "Removing it from this pack cancels it."}
            </DialogContentText>
            <DialogContentText variant="body2">
              {kind === "class" ? "You can rebook it if a seat is still available." : "You can rebook it if the slot is still available."}
            </DialogContentText>
          </DialogContent>
          <DialogActions disableSpacing sx={ACTIONS_SX}>
            <Button onClick={close} disabled={submitting} sx={BUTTON_SX}>
              Keep it
            </Button>
            <Button onClick={confirmCancel} variant="contained" color="error" disabled={submitting} sx={{ ...BUTTON_SX, color: "common.white" }}>
              {submitting ? <CircularProgress size="1.125rem" color="inherit" /> : `Cancel ${kind}`}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={TITLE_SX}>This {kind} stays booked</DialogTitle>
          <DialogContent sx={CONTENT_SX}>
            <DialogContentText sx={{ mb: 1.5 }}>
              {BLOCKED_REASON[preview.blockedReason] || "This can't be cancelled online, so the club has to cancel this"} {kind}. Contact the club if
              you can&apos;t make <strong>{target.label}</strong>.
            </DialogContentText>
            {!outsidePack && (
              <DialogContentText>
                You can still leave it out of this pack — it stays booked and unpaid, and you can pay for it from your dashboard.
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions disableSpacing sx={ACTIONS_SX}>
            {outsidePack ? (
              <Button onClick={close} variant="contained" sx={{ ...BUTTON_SX, color: "common.white" }}>
                OK
              </Button>
            ) : (
              <>
                <Button onClick={leaveOut} color="inherit" sx={{ ...BUTTON_SX, color: "text.secondary", mr: { sm: "auto" } }}>
                  Leave it out
                </Button>
                <Button onClick={close} variant="contained" sx={{ ...BUTTON_SX, color: "common.white" }}>
                  Keep it in the pack
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default ClubBookingCancelDialog;
